import { useMemo, useState } from 'react'
import { Icon } from 'semantic-ui-react'
import { useSelector } from '../../../../hooks/useSelector'
// @ts-ignore
import formatCost from '../../../../utils/formatCost'
import usePlayerActions from '../../hooks/usePlayerActions'
import { ConvertResourceModal } from '../ResourceButton/ConvertResourceModal'
import { getPopulationGrowth, truncateCash } from '@prometheus/utils'
import { createShipUpgradeLevels } from '@prometheus/utils'
import { calcQuest } from '../PlayerCashInvestments/calculateQuestBonus'
import classNames from 'classnames'
import { Button, Popup, ButtonProps, Card, Stack, Typography, TypographyColor, Box, Cards, CardProps } from '@prometheus/components'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import PromiseCard from '@/components/PromiseCard'
import { PopulationQuickBuy } from './PopulationQuickBuy'
import { getNextGrowthTier } from './populationTier'

export default () => {
  const population = useSelector(s => s.game.sheet?.population)
  const sheet = useSelector(s => s.game.sheet)
  const income = useSelector(s => s.game.sheet?.income) || 0
  const ship_growth = useSelector(s => s.game.options?.ship_growth) || []
  const _ship_upgrades = useSelector(s => s.game.options?.ship_upgrades) || []
  const is_map = useSelector(s => s.game.galaxy_display)
  const cash = useSelector(s => s.game.sheet?.cash) || 0
  const population_cost = useSelector(s => s.game.metadata?.population_cost) || 0
  const quest_bonus_pop_growth = useSelector(s => s.game.sheet?.quests.rewards.pop_growth_percentage)

  let quest_bonus = 0
  if (quest_bonus_pop_growth && population) {
    quest_bonus = Math.round(calcQuest(quest_bonus_pop_growth) * population.count)
  }

  const {
    onModifyCashAccounts,
    onConvertResource,
  } = usePlayerActions()
  const [buy, setBuy] = useState(false)

  const __ship_upgrades = useMemo(() => createShipUpgradeLevels(_ship_upgrades), [_ship_upgrades])
  const ship_upgrades = __ship_upgrades.slice(Math.max((population?.level || 0), 0), (population?.level || 0) + 6)
  const max_population = ship_upgrades[0]?.max || 35
  const count = population?.count || 0
  const next_population = useMemo(() => getPopulationGrowth(sheet, __ship_upgrades, ship_growth), [sheet, __ship_upgrades, ship_growth])
  const quick_buy_tier = useMemo(() => getNextGrowthTier({
    count,
    ship_growth,
    max_population,
    population_cost,
    cash,
  }), [count, ship_growth, max_population, population_cost, cash])

  const {
    onUpgrade,
  } = usePlayerActions()

  let incomeStatus: TypographyColor
  if (income < 0) {
    incomeStatus = 'danger'
  } else if (income === 0) {
    incomeStatus = 'text-muted'
  } else {
    incomeStatus = 'primary'
  }

  let cashStatus: ButtonProps['variant']
  if (cash < 0) {
    cashStatus = 'danger'
  } else if (cash === 0) {
    cashStatus = 'primary'
  } else {
    cashStatus = 'primary'
  }

  let populationStatus: TypographyColor
  let populationBorderStatus: CardProps['borderColor']
  if (max_population === count) {
    populationStatus = 'danger'
    populationBorderStatus = 'danger'
  } else if (max_population === next_population) {
    populationStatus = 'warning'
    populationBorderStatus = 'warning'
  } else {
    populationStatus = 'inherit'
    populationBorderStatus = undefined
  }

  return (
    <>
      <Card transparent className={classNames('pr-cash jr-ship', { is_map })} style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, zIndex: 1, position: 'relative', borderRight: 'none' }} padding="sm">
        <ConvertResourceModal open={buy} setOpen={setBuy} label="population" />
        <Stack>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--prometheus-spacing-base)' }}>
            <Typography retro variant="h4" color="primary">SHIP</Typography>
            <PromiseButtonV2 className='jr-cash' ignoreGameLogic onClick={() => onModifyCashAccounts('balance_cash')} variant={cashStatus} outline style={{ fontSize: '1.5rem' }}>Cash: {truncateCash(cash, {}, true)}</PromiseButtonV2>
          </div>
          <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 'var(--prometheus-spacing-base)' }}>
            <Card padding="none" className="jr-income" style={{ flexGrow: 1, padding: '8px 0' }}>
              <Typography align="center" variant="h3">INCOME</Typography>
              <Typography variant="h2" color={incomeStatus} align="center">{truncateCash(income, {}, true)}</Typography>
            </Card>
            <Popup
              position="bottom right"
              hoverable
              style={{ flexGrow: 1 }}
              trigger={(
                <Card interactive borderColor={populationBorderStatus} padding="none" style={{ padding: '8px 0' }}>
                  <Typography align="center" variant="h3">POPULATION</Typography>
                  <Typography color={populationStatus} variant="h2" align="center">{truncateCash(count, { raw: true })} / {truncateCash(max_population, { raw: true })}</Typography>
                </Card>
              )}
            >
              {closePopup => (
                <Card style={{ width: 'min(600px, calc(100vw - 20px))' }} padding="sm">
                  <Stack direction='row' style={{ flexWrap: 'wrap' }}>
                    <Stack style={{ flexBasis: '260px', flexGrow: 1, minWidth: '240px' }}>
                      <Typography variant="h4">Ship Growth</Typography>
                      <Button onClick={() => { closePopup(); return setBuy(true); }}>Hire Population</Button>
                      <PopulationQuickBuy
                        tier={quick_buy_tier}
                        onQuickBuy={(quantity) => onConvertResource('population', quantity).catch((e: Error) => { closePopup(); throw e })}
                      />
                      <Typography>
                        You can also convert 1 water into 3 people. Click the resource above.
                        <span style={{ color: 'var(--prometheus-primary)', fontWeight: 'bold' }}>
                          {quest_bonus > 0 ? `You are receiving an additional ${quest_bonus} pop/turn from quest rewards.` : ''}
                        </span>
                      </Typography>
                      <Typography variant="h4" color="tertiary">Population Growth Amount</Typography>
                      <Card padding='none' style={{ height: '250px', overflowY: 'auto' }}>
                        <Box space="sm">
                          {ship_growth.map((growth) => (
                            <Stack key={growth.count}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                                <Icon name="add user" />
                                <div>
                                  <Typography bold>&lt; {growth.count} Population</Typography>
                                  <Typography>+ {growth.growth} / year</Typography>
                                </div>
                              </div>
                            </Stack>
                          ))}
                        </Box>
                      </Card>
                    </Stack>
                    <div style={{ width: '1px', backgroundColor: 'var(--prometheus-border-light)' }} />
                    <Stack style={{ flexGrow: 1, minWidth: '240px' }}>
                      <Typography variant="h4">Upgrades - <Typography component="span" variant="h4" color="secondary">Level {(population?.level ?? 0) + 1}</Typography></Typography>
                      {populationStatus === 'warning' && (
                        <Typography color="warning" bold>Ship is about to be maxed!</Typography>
                      )}
                      {populationStatus === 'danger' && (
                        <Typography color="danger" bold>Ship is at full capacity!</Typography>
                      )}
                      <Cards>
                        {ship_upgrades.map((upgrade, i) => {
                          if (!population) { return <div key={i} /> }
                          const disabled = i > 1 || i === 0
                          const isNextUpgrade = i === 1
                          const isUpgraded = i < 1
                          let onClick
                          if (isNextUpgrade) {
                            onClick = () => onUpgrade('population_max').catch((e) => { closePopup(); throw e })
                          }
                          return (
                            <PromiseCard style={{ position: 'relative' }} padding="sm" ignoreGameLogic key={upgrade.max} disabled={disabled} interactive onClick={onClick}>
                              {isUpgraded && <Icon style={{ position: 'absolute', top: '10px', right: '4px', color: 'var(--prometheus-secondary)' }} size="large" name="check square" />}
                              {isNextUpgrade && (<Icon style={{ position: 'absolute', top: '10px', right: '4px', color: 'var(--prometheus-primary)' }} size="large" name="angle double up" />)}
                              <Typography color={isUpgraded ? 'secondary' : 'text-primary'} bold>Level {population.level + i + 1} | Max: {truncateCash(upgrade.max, { raw: true })}</Typography>
                              <p style={{ color: isUpgraded ? 'var(--prometheus-secondary)' : 'var(prometheus-light)' }}>{formatCost(upgrade.cost)}</p>
                            </PromiseCard>
                          )
                        })}
                      </Cards>
                    </Stack>
                  </Stack>
                </Card>
              )}
            </Popup>
          </div>
        </Stack>
      </Card>
    </>
  )
}
